import { Baby, Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ReactNode } from 'react';

export type BabyNameEntry = {
  name: string;
  gender: 'male' | 'female' | 'neutral';
};

interface SSWCircleProps {
  currentSSW: number;
  dayInWeek: number;
  babyNames: BabyNameEntry[];
  /** Optional title to display at top of circle (e.g., "Lungenreife") */
  title?: string;
  /** Override the progress percentage (0-100) for idle modes */
  overrideProgress?: number;
  /** Override the center label (e.g., "SSW 0" or birth date) */
  overrideLabel?: string;
  /** Override with custom content (takes precedence over overrideLabel) */
  overrideContent?: ReactNode;
}

export function SSWCircle({
  currentSSW,
  dayInWeek,
  babyNames,
  title,
  overrideProgress,
  overrideLabel,
  overrideContent,
}: SSWCircleProps) {
  // Calculate progress (SSW 1-42 = 0-100%)
  // Medical notation: currentSSW-1 shows completed weeks
  const completedWeeks = currentSSW - 1;
  const calculatedProgress = Math.min(100, Math.max(0, ((completedWeeks + dayInWeek / 7) / 40) * 100));

  // Use override progress if provided
  const progressPercent = overrideProgress !== undefined ? overrideProgress : calculatedProgress;

  // Gender-based styling for badges
  const genderStyles = {
    male: 'bg-pp-blue-boy',
    female: 'bg-pp-pink-girl',
    neutral: 'bg-pp-neutral',
  };

  // Organize names into rows (pairs of 2 on top, single at bottom if odd)
  const organizeNames = (names: BabyNameEntry[]) => {
    if (names.length === 0) return { topRow: [], bottomRow: [] };
    if (names.length === 1) return { topRow: [], bottomRow: names };
    if (names.length === 2) return { topRow: names, bottomRow: [] };
    if (names.length === 3) return { topRow: names.slice(0, 2), bottomRow: names.slice(2) };
    // 4 or more: 2 on top, rest on bottom
    return { topRow: names.slice(0, 2), bottomRow: names.slice(2) };
  };

  const { topRow, bottomRow } = organizeNames(babyNames);

  return (
    <div className="ssw-circle-container relative w-[50vw] max-w-[400px] min-w-[250px] aspect-square mx-auto">
      {/* Background circle (remaining time) */}
      <div className="absolute inset-0 rounded-full bg-white/10" />

      {/* Progress circle (centered, scales with progress) */}
      <div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-pp-teal border border-pp-teal-light transition-all duration-300"
        style={{
          width: `${progressPercent}%`,
          height: `${progressPercent}%`,
        }}
      />

      {/* Title Badge - at top of circle, auto-width max 80% */}
      {title && (
        <div className="absolute -top-2 left-1/2 -translate-x-1/2 z-20 max-w-[80%]">
          <div className="glass-effect-light bg-pp-neutral rounded-[15px] px-4 py-1.5 flex items-center justify-center gap-2">
            <Sparkles className="w-5 h-5 text-white flex-shrink-0" />
            <span className="text-white text-sm font-medium text-center leading-tight">{title}</span>
          </div>
        </div>
      )}

      {/* Content overlay */}
      <div className="absolute inset-0 flex flex-col items-center justify-center z-10" style={{ lineHeight: '3.5rem' }}>
        {overrideContent ? (
          // Custom content mode (takes precedence)
          overrideContent
        ) : overrideLabel ? (
          // Custom label mode (e.g., birth date or "SSW 0")
          <>
            {overrideLabel === 'SSW 0' ? (
              <>
                <span className="text-white font-semibold tracking-wide text-3xl">SSW</span>
                <span className="text-white font-bold text-8xl">0</span>
              </>
            ) : (
              // Birth date or custom label
              <span className="text-white font-bold text-2xl text-center px-4">{overrideLabel}</span>
            )}
          </>
        ) : (
          // Normal SSW mode
          <>
            <span className="text-white font-semibold tracking-wide text-3xl">SSW</span>
            <div className="flex items-center">
              <span className="text-white font-bold text-8xl">{completedWeeks}</span>
              <span className="font-bold italic text-6xl text-pp-dark">+{dayInWeek}</span>
            </div>
          </>
        )}
      </div>

      {/* Baby Names Badges - positioned at bottom of circle */}
      {babyNames.length > 0 && (
        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex flex-col-reverse items-center gap-1.5 z-20">
          {/* Bottom row */}
          {bottomRow.length > 0 && (
            <div className="flex justify-center gap-2">
              {bottomRow.map((entry, index) => (
                <div
                  key={`bottom-${index}`}
                  className={cn(
                    'glass-effect-light rounded-full px-3 py-1 flex items-center gap-1.5',
                    genderStyles[entry.gender]
                  )}
                >
                  <Baby className="w-3.5 h-3.5 text-white" />
                  <span className="text-white text-xs font-medium">{entry.name}</span>
                </div>
              ))}
            </div>
          )}

          {/* Top row */}
          {topRow.length > 0 && (
            <div className="flex justify-center gap-2">
              {topRow.map((entry, index) => (
                <div
                  key={`top-${index}`}
                  className={cn(
                    'glass-effect-light rounded-full px-3 py-1 flex items-center gap-1.5',
                    genderStyles[entry.gender]
                  )}
                >
                  <Baby className="w-3.5 h-3.5 text-white" />
                  <span className="text-white text-xs font-medium">{entry.name}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
